import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box, Container, Grid, Paper, Stack, Group, Text, Button, Badge,
  TextInput, NumberInput, Select, Textarea, Tabs, Table, Progress,
  Accordion, Divider, Alert, ScrollArea, Tooltip,
} from '@mantine/core';
import {
  IconRefresh, IconDownload, IconUpload, IconPlus, IconTrash,
  IconMail, IconInfoCircle, IconAlertTriangle, IconArrowLeft, IconCloudUpload,
} from '@tabler/icons-react';
import Footer from '../../components/Footer';
import { exportEccoToExcel, importEccoFromExcel } from './services/eccoExcelExport';

// ── Constants ────────────────────────────────────────────────────────────────
const MARGIN_STD     = 0.25;
const MARGIN_ONBOARD = 0.40;
const MARGIN_SYNTH   = 0.50;
const MARGIN_VIGTRN  = 0.20;
const HRS_ADV        = 2.575;
const HRS_BASIC      = 1.25;
const HRS_THIRD      = 1.87;
const LS_KEY         = 'ecco-calculator-v1';

const TIERS         = ['Cloud Select Plus', 'Cloud Select', 'Cloud Reactive'];
const WX_OPTIONS    = ['Cloud Select', 'Cloud Reactive', 'None'];
const PLATFORMS     = ['WxCC', 'Five9', 'Genesys', 'Twilio', 'Amazon Connect', 'Webex Connect'];
const AI_PLATFORMS  = ['Cisco AI', 'Five9 AI', 'Genesys AI', 'Amazon AI', 'Google AI', 'Other'];
const CONTRACT_TYPES = ['New', 'Add On', 'Renewal', 'One-Time Only'];
const SA_NAMES      = ['Pavan Dave', 'Morgan Andreas', 'Court Swenson', 'Bill Webb', 'Chad Dotson'];
const BDM_NAMES     = ['Jeanne Farrenkothen', 'Jay Ward', 'James McGehee', 'Aaron Thatcher'];
const TIER_DESC     = {
  'Cloud Select Plus': 'B/F 0.04 + MACD 0.06 · Scripting · SDM · Synthetic',
  'Cloud Select':      'B/F 0.04 + MACD 0.06 · Scripting · SDM',
  'Cloud Reactive':    'B/F 0.05 only · No MACD/Scripting/SDM',
};
// Migrates saved sessions from the old WX_OPTIONS labels ('Standard'/'Reactive') to
// the current ones ('Cloud Select'/'Cloud Reactive') that match the source workbook's
// dropdown values.
const migrateWx = wx => wx === 'Standard' ? 'Cloud Select' : wx === 'Reactive' ? 'Cloud Reactive' : wx;

const BILLING_OPTIONS = ['Monthly', 'Annual', 'One-Time', 'Total In Advance'];

let _chId = 0;
const nextChId = () => ++_chId;

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmt  = (n, d = 2) => '$' + (isNaN(n) ? '0' : Number(n).toFixed(d)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const fmtD = (n, d = 2) => Math.abs(n) < 0.005 ? '—' : fmt(n, d); // dash for zero-valued cells
const fmtN = (n, d = 2) => Number(n).toFixed(d).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const pct  = n => (n * 100).toFixed(1) + '%';
const rnd2 = n => Math.round(n * 100) / 100;
const price = (cost, margin) => rnd2(cost / (1 - margin));

// Stable SKU row key
const skuKey = r => r.sku + '|' + r.desc.slice(0, 40);

// ── Calculation engine ───────────────────────────────────────────────────────
function calcEcco(state) {
  const { projectInfo, tier, wx, config, addOns, channels, rates } = state;
  const term    = Math.max(1, +projectInfo.contractTerm || 12);
  const agents  = +config.agents  || 0;
  const ai      = +config.ai      || 0;
  const tp      = +config.thirdParty || 0;
  const dialer  = +config.dialerPorts || 0;
  const ivrP    = +config.ivrPorts;
  const dcEng   = +rates.dcEngCost || 75;
  const esrRate = +rates.esrRate   || 195;
  const engCost = +rates.engCost   || 150;
  const R_synthSetup       = +rates.synthSetupFee         || 500;
  const R_synthSetupAddon  = +rates.synthSetupFeeAddon    || 500;
  const R_synthAnnual      = +rates.synthAnnualCost       || 1000;
  const R_synthAnnualAddon = +rates.synthAnnualCostAddon  || 1000;
  const R_onbStd           = +rates.onboardingFeeStd      || 500;
  const R_onbPrem          = +rates.onboardingFeePrem     || 800;
  const R_snCost           = +rates.vigilusServiceNowCost || 1000;
  const R_trainPerSess     = +rates.vigilusTrainingPerSession || 200;
  const R_synthMinsInc     = +rates.synthMinsIncluded     || 10000;
  const R_synthMinsAddon   = +rates.synthMinsPerAddon     || 10000;

  const hrsCost = (hrs) => hrs * dcEng / term;

  // Row 3 – Channels + Third Party
  const advQty = channels.filter(c => c.category === 'Advanced').reduce((s, c) => s + (parseFloat(c.qty) || 1), 0);
  const basQty = channels.filter(c => c.category === 'Basic').reduce((s, c) => s + (parseFloat(c.qty) || 1), 0);
  const C3 = (HRS_ADV * advQty * term) + (HRS_BASIC * basQty * term);
  const D3 = tp > 0 ? HRS_THIRD * tp * term : 0;
  const E3cmo = hrsCost(C3 + D3);
  const E3pmo = price(E3cmo, MARGIN_STD);

  // Row 4 – Agent + AI
  let C4, D4;
  if (tier === 'Cloud Reactive') {
    C4 = ((agents * 0.05) + (ai * 0.025)) * term; D4 = 0;
  } else {
    C4 = ((agents * 0.04) + (ai * 0.02)) * term;
    D4 = ((agents * 0.06) + (ai * 0.03)) * term;
  }
  const E4cmo = hrsCost(C4 + D4);
  const E4pmo = price(E4cmo, MARGIN_STD);

  // Row 5 – Scripting
  let C5 = 0, D5 = 0;
  if (tier !== 'Cloud Reactive') {
    C5 = ((0.008 * ivrP * term) + (0.004 * ai * term));
    D5 = (0.016 * ivrP * term) + (0.008 * ai * term);
  }
  const E5cmo = hrsCost(C5 + D5);
  const E5pmo = price(E5cmo, MARGIN_STD);

  // Row 6 – Dialer. Per source model: BF hours apply whenever Webex Calling is
  // configured (any option besides None); MACD hours apply only at the
  // "Cloud Select" tier specifically (not Cloud Select Plus or Reactive).
  const C6    = wx !== 'None' ? 0.00625 * dialer * term : 0;
  const D6    = tier === 'Cloud Select' ? 0.01 * dialer * term : 0;
  const E6cmo = hrsCost(C6 + D6);
  const E6pmo = price(E6cmo, MARGIN_STD);

  // Row 7 – Customer Provided Ticket Count Adjustment. If the customer's actual/
  // expected ticket volume (config.custTickets) exceeds what rows 2-6 already
  // estimate, add the delta back in as extra BF/MACD hours. Ticket counts are
  // hours-per-ticket-implied (2 hrs/BF ticket, 1 hr/MACD ticket), then the
  // overage is split back into hours using the same BF/MACD ticket-mix ratio
  // (with a fixed 0.4hr/10% MACD weighting, mirroring the source model).
  const HRS_PER_BF_TICKET = 2;
  const HRS_PER_MACD_TICKET = 1;
  const estBFTickets   = (C3 + C4 + C5 + C6) / HRS_PER_BF_TICKET;
  const estMACDTickets = (D3 + D4 + D5 + D6) / HRS_PER_MACD_TICKET;
  const estTicketTotal = estBFTickets + estMACDTickets;
  const autoBFPct   = estTicketTotal > 0 ? estBFTickets   / estTicketTotal : 0;
  const autoMACDPct = estTicketTotal > 0 ? estMACDTickets / estTicketTotal : 0;
  // Allow user to override the BF/MACD ticket split (used by row 7 adjustment)
  const bfPctOverride = config.ticketSplitManual ? Math.min(1, Math.max(0, (+config.bfPctManual || 0) / 100)) : autoBFPct;
  const macdPctOverride = 1 - bfPctOverride;
  const estBFPct   = bfPctOverride;
  const estMACDPct = macdPctOverride;
  const custTickets = +config.custTickets || 0;
  const bfTicketDelta = tier === 'Cloud Select'
    ? Math.max(0, estBFPct * custTickets - estBFTickets)
    : Math.max(0, custTickets - estBFTickets);
  const macdTicketDelta = Math.max(0, estMACDPct * custTickets - estMACDTickets);
  const C7 = HRS_PER_BF_TICKET * (estBFPct * (bfTicketDelta + macdTicketDelta));
  const D7 = 0.4 * (0.1 * (bfTicketDelta + macdTicketDelta));
  const E7cmo = hrsCost(C7 + D7);
  const E7pmo = price(E7cmo, MARGIN_STD);

  // Row 8 – SDM
  let C8 = 0, D8 = 0;
  if (tier !== 'Cloud Reactive') {
    C8 = ((0.002 * agents) + (0.001 * ai)) * term;
    D8 = ((0.004 * agents) + (0.002 * ai)) * term;
  }
  const E8cmo = hrsCost(C8 + D8);
  const E8pmo = price(E8cmo, MARGIN_STD);

  // Row 14 – Main CC support
  const J14cmo = E4cmo + E5cmo + E6cmo + E7cmo + E8cmo;
  const I14pmo = price(J14cmo, MARGIN_STD);

  // Row 18 – Webex Calling
  const wxCpa   = wx === 'Cloud Reactive' ? 1.95 : wx === 'Cloud Select' ? 3.22 : 0;
  const J18cmo  = wxCpa * agents;
  const I18pmo  = J18cmo > 0 ? price(J18cmo, MARGIN_STD) : 0;

  // Row 16/17 – Vigilus Synthetic Transactions (recurring monthly vendor pass-through).
  // Included minutes still carry a real Presidio cost ($1,000/yr) even though the
  // minutes themselves are "free" to a Cloud Select Plus customer. The add-on row's
  // recurring cost is driven by the add-on NUMBER count (Factors!B57, synthCount),
  // not the annual-minutes field — the two are tracked separately in the source
  // model (annualMinutes only drives the description label and the ServiceNow
  // integration auto-trigger), at the same $1,000/yr-per-number rate.
  const J16cmo     = tier === 'Cloud Select Plus' ? R_synthAnnual / 12 : 0;
  const I16pmo     = J16cmo > 0 ? price(J16cmo, MARGIN_SYNTH) : 0;
  const synthCount = +addOns.synthCount || 0;
  const annualMin  = +addOns.annualMinutes || 0;
  const J17cmo     = synthCount > 0 ? (synthCount * R_synthAnnualAddon) / 12 : 0;
  const I17pmo     = J17cmo > 0 ? price(J17cmo, MARGIN_SYNTH) : 0;

  // One-time
  const platform   = projectInfo.platform || 'WxCC';
  // Cloud Onboarding auto-applies for New contracts only (Factors!B49 / B38=AA35="New").
  const I26price   = projectInfo.contractType === 'New' ? ((platform === 'Five9' || platform === 'Genesys') ? R_onbPrem : R_onbStd) : 0;
  const J26cost    = I26price * (1 - MARGIN_ONBOARD);
  // Vigilus ServiceNow Integration auto-applies for Cloud Select Plus or whenever a
  // synthetic minutes add-on is configured (Factors!B50: B36="Cloud Select Plus" OR B56>0).
  const J27cost    = (tier === 'Cloud Select Plus' || annualMin > 0) ? R_snCost : 0;
  const I27price   = J27cost > 0 ? price(J27cost, MARGIN_VIGTRN) : 0;
  const K19price   = (+addOns.electiveHours || 0) * esrRate;
  const L19cost    = (+addOns.electiveHours || 0) * ((dcEng + engCost) / 2);
  const J25cost    = (+addOns.vigilusSessions || 0) * R_trainPerSess;
  const I25price   = J25cost > 0 ? price(J25cost, MARGIN_VIGTRN) : 0;
  const J24cost    = synthCount * R_synthSetupAddon;
  const I24price   = J24cost > 0 ? price(J24cost, MARGIN_SYNTH) : 0;
  const J23cost    = tier === 'Cloud Select Plus' ? R_synthSetup : 0;
  const I23price   = J23cost > 0 ? price(J23cost, MARGIN_SYNTH) : 0;

  const recurringPriceMo = I14pmo + E3pmo + I18pmo + I16pmo + I17pmo;
  const recurringCostMo  = J14cmo + E3cmo + J18cmo + J16cmo + J17cmo;
  const oneTimePrice     = I26price + I27price + K19price + I25price + I24price + I23price;
  const oneTimeCost      = J26cost  + J27cost  + L19cost  + J25cost  + J24cost  + J23cost;
  const totalValue       = recurringPriceMo * term + oneTimePrice;
  const totalCost        = recurringCostMo  * term + oneTimeCost;
  const marginAmt        = totalValue - totalCost;
  const marginPct        = totalValue > 0 ? marginAmt / totalValue : 0;

  return {
    C3, D3, E3cmo, E3pmo, C4, D4, E4cmo, E4pmo,
    C5, D5, E5cmo, E5pmo, C6, D6, E6cmo, E6pmo,
    C7, D7, E7cmo, E7pmo,
    C8, D8, E8cmo, E8pmo,
    J14cmo, I14pmo, J18cmo, I18pmo,
    J16cmo, I16pmo, J17cmo, I17pmo,
    I26price, J26cost, I27price, J27cost,
    K19price, L19cost, I25price, J25cost,
    I24price, J24cost, I23price, J23cost,
    recurringPriceMo, recurringCostMo,
    oneTimePrice, oneTimeCost, totalValue, totalCost, marginAmt, marginPct,
    advQty, basQty, totalChQty: advQty + basQty,
    ivrPorts: ivrP, wxCpa, term, vigilusServiceNow: J27cost > 0, dcEng,
    // ticket split info for OEF table footer
    autoBFPct, autoMACDPct, estBFPct, estMACDPct,
    // estimated monthly ticket count: ROUNDUP(SUM(I2:J8)/term) — spreadsheet hardcodes SDM
    // ticket columns (I8/J8) to 0, so SDM hours do NOT contribute to this count.
    estMonthlyTicketsRaw: ((C3+C4+C5+C6+C7)/HRS_PER_BF_TICKET + (D3+D4+D5+D6+D7)/HRS_PER_MACD_TICKET) / term,
    estMonthlyTickets: Math.ceil(((C3+C4+C5+C6+C7)/HRS_PER_BF_TICKET + (D3+D4+D5+D6+D7)/HRS_PER_MACD_TICKET) / term),
  };
}

// ── Tier / Wx selector button ────────────────────────────────────────────────
function SelectBtn({ label, sub, active, onClick }) {
  return (
    <Box
      onClick={onClick}
      style={{
        flex: 1, border: `2px solid ${active ? 'var(--mantine-color-blue-6)' : 'var(--mantine-color-gray-3)'}`,
        borderRadius: 6, padding: '8px 6px', textAlign: 'center', cursor: 'pointer',
        background: active ? 'var(--mantine-color-blue-0)' : 'white',
      }}
    >
      <Box style={{ width: 8, height: 8, borderRadius: '50%', margin: '0 auto 5px', background: active ? 'var(--mantine-color-blue-6)' : 'var(--mantine-color-gray-4)' }} />
      <Text size="xs" fw={700} c={active ? 'blue' : 'dark'}>{label}</Text>
      {sub && <Text size="9px" c="dimmed" style={{ lineHeight: 1.3, marginTop: 2 }}>{sub}</Text>}
    </Box>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function EccoCalculatorApp({ onBackToPlatformSelector, platformInfo }) {
  const [projectInfo, setProjectInfo] = useState({
    customerName: '', oppNum: '', amName: '', saName: '', bdmName: '',
    platform: 'WxCC', contractType: 'New', contractTerm: 12,
  });
  const [tier, setTier]     = useState('Cloud Reactive');
  const [wx, setWx]         = useState('None');
  const [config, setConfig] = useState({
    agents: 0, ai: 0, emailEP: 0, chatEP: 0,
    ivrPorts: 0, ivrPortsManual: false,
    dialerPorts: 0, thirdParty: 0, thirdPartyNotes: '', custTickets: 0,
    ticketSplitManual: false, bfPctManual: 25,
  });
  const [addOns, setAddOns] = useState({
    electiveHours: 0, vigilusSessions: 0, synthCount: 0, annualMinutes: 0,
  });
  const [channels, setChannels] = useState([]);
  const [rates, setRates] = useState({
    // Labor / delivery rates
    dcEngCost: 75, esrRate: 195, engCost: 150, targetMargin: 25,
    // Vigilus / Add-on pricing (Factors sheet B45–B53)
    synthSetupFee: 500,        // B45 – one-time setup, 1 number included (Select Plus)
    synthSetupFeeAddon: 500,   // B46 – one-time setup per add-on dialed number
    synthAnnualCost: 1000,     // B47 – annual vendor cost for included number ($1k/yr → /12 monthly)
    synthAnnualCostAddon: 1000,// B48 – annual vendor cost per add-on number
    onboardingFeeStd: 500,     // B49 – Cloud Onboarding price (WxCC / Twilio / Amazon)
    onboardingFeePrem: 800,    // B49 – Cloud Onboarding price (Five9 / Genesys)
    vigilusServiceNowCost: 1000,    // B50 – Vigilus ServiceNow integration cost
    vigilusTrainingPerSession: 200, // B51 – Vigilus Training cost per session/hour
    synthMinsIncluded: 10000,  // B52 – minutes included per year (description label)
    synthMinsPerAddon: 10000,  // B53/B56 – minutes per add-on increment (description label)
  });
  const [activeTab, setActiveTab] = useState('factors');
  const [oefMultipliersOpen, setOefMultipliersOpen] = useState(false);
  const [skuMarginOverrides, setSkuMarginOverrides] = useState({});
  const [skuBillingOverrides, setSkuBillingOverrides] = useState({});
  const [restored, setRestored]   = useState(false);
  const importRef    = useRef(null);
  const xlsxImportRef = useRef(null);

  // Persistence
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.projectInfo) setProjectInfo(d.projectInfo);
        if (d.tier)     setTier(d.tier);
        if (d.wx)       setWx(migrateWx(d.wx));
        if (d.config)   setConfig(d.config);
        if (d.addOns)   setAddOns(d.addOns);
        if (d.channels) { setChannels(d.channels); _chId = Math.max(_chId, ...d.channels.map(c => c.id || 0)); }
        if (d.rates)    setRates(d.rates);
        if (d.skuMarginOverrides) setSkuMarginOverrides(d.skuMarginOverrides);
        if (d.skuBillingOverrides) setSkuBillingOverrides(d.skuBillingOverrides);
      }
    } catch {}
    setRestored(true);
  }, []);

  useEffect(() => {
    if (!restored) return;
    try { localStorage.setItem(LS_KEY, JSON.stringify({ projectInfo, tier, wx, config, addOns, channels, rates, skuMarginOverrides, skuBillingOverrides })); } catch {}
  }, [projectInfo, tier, wx, config, addOns, channels, rates, skuMarginOverrides, skuBillingOverrides, restored]);

  // Keep IVR Ports in sync with Licensed Agents unless the user has manually overridden it.
  // Mirrors the spreadsheet's B33 = "=B29" default (Factors sheet).
  useEffect(() => {
    if (!config.ivrPortsManual) {
      setConfig(prev => ({ ...prev, ivrPorts: prev.agents }));
    }
  }, [config.agents, config.ivrPortsManual]);

  // Auto-set rules for Cloud Select Plus
  useEffect(() => {
    if (tier === 'Cloud Select Plus') {
      setAddOns(prev => ({
        ...prev,
        vigilusSessions:  (projectInfo.contractType === 'New' && prev.vigilusSessions === 0) ? 1 : prev.vigilusSessions,
      }));
    }
  }, [tier, projectInfo.contractType]);

  const calc = useMemo(() => calcEcco({ projectInfo, tier, wx, config, addOns, channels, rates }), [
    projectInfo, tier, wx, config, addOns, channels, rates,
  ]);

  // Updaters
  const pi  = (f, v) => setProjectInfo(p => ({ ...p, [f]: v }));
  const cfg = (f, v) => setConfig(p => ({ ...p, [f]: v }));
  const ao  = (f, v) => setAddOns(p => ({ ...p, [f]: v }));
  const rt  = (f, v) => setRates(p => ({ ...p, [f]: v }));

  // Channel management
  const addChannel    = () => setChannels(p => [...p, { id: nextChId(), name: 'Channel ' + _chId, types: ['Voice'], category: 'Advanced', qty: 1, platform: '', deployment: 'Cloud', integration: '', businessObjective: '', useCases: '', languages: 'English', notes: '' }]);
  const removeChannel = id  => setChannels(p => p.filter(c => c.id !== id));
  const updCh         = (id, f, v) => setChannels(p => p.map(c => c.id === id ? { ...c, [f]: v } : c));
  const toggleType    = (id, t)    => setChannels(p => p.map(c => {
    if (c.id !== id) return c;
    const types = c.types.includes(t) ? (c.types.length > 1 ? c.types.filter(x => x !== t) : c.types) : [...c.types, t];
    return { ...c, types };
  }));

  // Export / Import / Reset
  const [xlsxExporting, setXlsxExporting] = useState(false);
  const exportToExcel = async () => {
    setXlsxExporting(true);
    try {
      await exportEccoToExcel({ projectInfo, tier, wx, config, addOns, channels, rates });
    } catch (e) {
      alert('Export failed: ' + e.message);
    } finally {
      setXlsxExporting(false);
    }
  };

  const importFromExcel = async e => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    try {
      const patch = await importEccoFromExcel(file);
      resetForm();
      if (patch.projectInfo) setProjectInfo(prev => ({ ...prev, ...patch.projectInfo }));
      if (patch.tier) setTier(patch.tier);
      if (patch.wx)   setWx(migrateWx(patch.wx));
      if (patch.config) setConfig(prev => ({ ...prev, ...patch.config }));
      if (patch.addOns) setAddOns(prev => ({ ...prev, ...patch.addOns }));
      if (patch.rates)  setRates(prev => ({ ...prev, ...patch.rates }));
      // Rebuild individual channels from the B54 (Advanced) / B55 (Basic) counts,
      // named from the D54/D55 notes column when present.
      if (patch.channels) {
        setChannels(patch.channels);
        _chId = Math.max(_chId, ...patch.channels.map(c => c.id || 0));
      }
    } catch (err) {
      alert('Import failed: ' + err.message);
    }
  };

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify({ exportedAt: new Date().toISOString(), modelVersion: 'ECCO v2.8', projectInfo, tier, wx, config, addOns, channels, rates, skuMarginOverrides, skuBillingOverrides }, null, 2)], { type: 'application/json' });
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: `ECCO_Quote_${(projectInfo.customerName || 'ECCO').replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().slice(0, 10)}.json` });
    a.click(); URL.revokeObjectURL(a.href);
  };
  const importJSON = e => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const d = JSON.parse(ev.target.result);
        if (d.projectInfo) setProjectInfo(d.projectInfo);
        if (d.tier) setTier(d.tier); if (d.wx) setWx(migrateWx(d.wx));
        if (d.config) setConfig(d.config); if (d.addOns) setAddOns(d.addOns);
        if (d.channels) { setChannels(d.channels); _chId = Math.max(_chId, ...d.channels.map(c => c.id || 0)); }
        if (d.rates) setRates(d.rates);
        if (d.skuMarginOverrides) setSkuMarginOverrides(d.skuMarginOverrides);
        if (d.skuBillingOverrides) setSkuBillingOverrides(d.skuBillingOverrides);
      } catch { alert('Failed to import. Make sure this is a valid ECCO quote export.'); }
    };
    reader.readAsText(file); e.target.value = '';
  };
  const resetForm = () => {
    setProjectInfo({ customerName: '', oppNum: '', amName: '', saName: '', bdmName: '', platform: 'WxCC', contractType: 'New', contractTerm: 12 });
    setTier('Cloud Reactive'); setWx('None');
    setConfig({ agents: 0, ai: 0, emailEP: 0, chatEP: 0, ivrPorts: 0, ivrPortsManual: false, dialerPorts: 0, thirdParty: 0, thirdPartyNotes: '', custTickets: 0, ticketSplitManual: false, bfPctManual: 25 });
    setAddOns({ electiveHours: 0, vigilusSessions: 0, synthCount: 0, annualMinutes: 0 });
    setChannels([]);
    setRates({ dcEngCost: 75, esrRate: 195, engCost: 150, targetMargin: 25, synthSetupFee: 500, synthSetupFeeAddon: 500, synthAnnualCost: 1000, synthAnnualCostAddon: 1000, onboardingFeeStd: 500, onboardingFeePrem: 800, vigilusServiceNowCost: 1000, vigilusTrainingPerSession: 200, synthMinsIncluded: 10000, synthMinsPerAddon: 10000 });
    setSkuMarginOverrides({});
    setSkuBillingOverrides({});
  };

  const marginColor = calc.marginPct >= rates.targetMargin / 100 ? 'green' : calc.marginPct >= (rates.targetMargin / 100) * 0.8 ? 'yellow' : 'red';

  const oefRows = [
    { n: 1, desc: 'Platform Support Services', bf: 0, macd: 0, cmo: 0, pmo: 0 },
    { n: 2, desc: `Additional Channel Support / Third Party${config.thirdParty > 0 ? ` (${config.thirdParty} app${config.thirdParty > 1 ? 's' : ''}${config.thirdPartyNotes ? ' – ' + config.thirdPartyNotes : ''})` : ''}`, bf: calc.C3, macd: calc.D3, cmo: calc.E3cmo, pmo: calc.E3pmo },
    { n: 3, desc: `Agent & AI Assistant Support (${config.agents} agents, ${config.ai} AI)`, bf: calc.C4, macd: calc.D4, cmo: calc.E4cmo, pmo: calc.E4pmo },
    { n: 4, desc: `Scripting Support${tier === 'Cloud Reactive' ? ' (N/A – Reactive)' : ''}`, bf: calc.C5, macd: calc.D5, cmo: calc.E5cmo, pmo: calc.E5pmo },
    { n: 5, desc: `Dialer Support${wx === 'None' && tier !== 'Cloud Select' ? ' (N/A)' : ''}`, bf: calc.C6, macd: calc.D6, cmo: calc.E6cmo, pmo: calc.E6pmo },
    { n: 6, desc: 'Customer Provided Ticket Count Adjustment', bf: calc.C7, macd: calc.D7, cmo: calc.E7cmo, pmo: calc.E7pmo },
    { n: 7, desc: `CC SDM Uplift${tier === 'Cloud Reactive' ? ' (N/A – Reactive)' : ''}`, bf: calc.C8, macd: calc.D8, cmo: calc.E8cmo, pmo: calc.E8pmo },
  ];

  // Build skuRows
  const skuRows = [];
  // Build agent description matching spreadsheet Row 14 formula exactly
  const agentAddons = [
    config.ai > 0 ? `${config.ai} AI Assistant(s)` : null,
    config.emailEP > 0 ? `${config.emailEP} Email EntryPoint(s)` : null,
    config.chatEP > 0 ? `${config.chatEP} Social EntryPoint(s)` : null,
  ].filter(Boolean);
  const agentDesc = `${tier} ${projectInfo.platform}: (${config.agents}) Agents${agentAddons.length ? ' (with ' + agentAddons.join(', ') + ')' : ''}`;

  // Build channel description matching spreadsheet Row 15 formula exactly
  const chParts = [
    calc.advQty > 0 ? `(${calc.advQty}) Adv Agentless Channel(s)` : null,
    calc.basQty > 0 ? `(${calc.basQty}) Basic Agentless Channel(s)` : null,
    config.thirdParty > 0 ? `(${config.thirdParty}) Third Party Application(s)` : null,
  ].filter(Boolean);
  const chDesc = chParts.join(' ');

  if (config.agents > 0 || config.ai > 0) skuRows.push({ sku: 'MS-CS-CCIM', recurring: true, desc: agentDesc, type: projectInfo.contractType, term: calc.term, pmo: calc.I14pmo, cmo: calc.J14cmo, ep: calc.I14pmo * calc.term, ec: calc.J14cmo * calc.term, mgn: MARGIN_STD });
  if (calc.totalChQty > 0 || config.thirdParty > 0) skuRows.push({ sku: 'MS-CS-CCIM', recurring: true, desc: chDesc, type: projectInfo.contractType, term: calc.term, pmo: calc.E3pmo, cmo: calc.E3cmo, ep: calc.E3pmo * calc.term, ec: calc.E3cmo * calc.term, mgn: MARGIN_STD });
  if (calc.J16cmo > 0) skuRows.push({ sku: 'MS-CS-CCIM', recurring: true, hideVendorCost: true, desc: `Vigilus Synthetic Transactions - ${rates.synthMinsIncluded?.toLocaleString() ?? '10,000'} Minutes per year included Cloud Select Plus`, type: projectInfo.contractType, term: calc.term, pmo: calc.I16pmo, cmo: calc.J16cmo, ep: calc.I16pmo * calc.term, ec: calc.J16cmo * calc.term, mgn: MARGIN_SYNTH });
  if (calc.J17cmo > 0) skuRows.push({ sku: 'MS-CS-CCIM', recurring: true, hideVendorCost: true, desc: `Vigilus Synthetic Transactions - ${(addOns.annualMinutes * (rates.synthMinsPerAddon || 10000)).toLocaleString()} Minutes per year Add-On`, type: projectInfo.contractType, term: calc.term, pmo: calc.I17pmo, cmo: calc.J17cmo, ep: calc.I17pmo * calc.term, ec: calc.J17cmo * calc.term, mgn: MARGIN_SYNTH });
  if (calc.J18cmo > 0) skuRows.push({ sku: 'MS-CS-TUCM', recurring: true, desc: `${wx} ${projectInfo.platform}: ${config.agents} Agents, Webex Calling - Call Control for Contact Center Agents`, type: projectInfo.contractType, term: calc.term, pmo: calc.I18pmo, cmo: calc.J18cmo, ep: calc.I18pmo * calc.term, ec: calc.J18cmo * calc.term, mgn: MARGIN_STD });
  if (calc.K19price > 0) skuRows.push({ sku: 'MS-SE-CCEH', recurring: false, desc: `${addOns.electiveHours} Elective Service Hours @ $${rates.esrRate} an hour`, type: 'Total In Advance', term: 1, pmo: calc.K19price, cmo: calc.L19cost, ep: calc.K19price, ec: calc.L19cost, mgn: calc.K19price > 0 ? (calc.K19price - calc.L19cost) / calc.K19price : 0 });
  if (calc.K19price > 0) skuRows.push({ sku: 'MS-CP', recurring: true, desc: `Line for ${addOns.electiveHours} ESR hours to be billed in ChangePoint`, type: projectInfo.contractType, term: calc.term, pmo: 0, cmo: 0, ep: 0, ec: 0, mgn: 0 });
  if (calc.I26price > 0) skuRows.push({ sku: 'MS-SM-STM', recurring: false, desc: 'PMN Cloud Onboarding', type: 'One Time Fee', term: 1, pmo: calc.I26price, cmo: calc.J26cost, ep: calc.I26price, ec: calc.J26cost, mgn: MARGIN_ONBOARD });
  if (calc.I27price > 0) skuRows.push({ sku: 'MS-SM-STM', recurring: false, desc: 'Vigilus On Boarding', type: 'One Time Fee', term: 1, pmo: calc.I27price, cmo: calc.J27cost, ep: calc.I27price, ec: calc.J27cost, mgn: MARGIN_VIGTRN });
  if (calc.I23price > 0) skuRows.push({ sku: 'MS-SM-STM', recurring: false, desc: 'Vigilus Synthetic Transactions - 1 Number Included Cloud Select Plus', type: 'One Time Fee', term: 1, pmo: calc.I23price, cmo: calc.J23cost, ep: calc.I23price, ec: calc.J23cost, mgn: MARGIN_SYNTH });
  if (calc.I24price > 0) skuRows.push({ sku: 'MS-SM-STM', recurring: false, desc: `Vigilus Synthetic Transactions - ${addOns.synthCount} Numbers Add-On`, type: 'One Time Fee', term: 1, pmo: calc.I24price, cmo: calc.J24cost, ep: calc.I24price, ec: calc.J24cost, mgn: MARGIN_SYNTH });
  if (calc.I25price > 0) skuRows.push({ sku: 'MS-SM-STM', recurring: false, desc: `Vigilus Training and Knowledge Transfer - ${addOns.vigilusSessions} Hours`, type: 'One-Time', term: 1, pmo: calc.I25price, cmo: calc.J25cost, ep: calc.I25price, ec: calc.J25cost, mgn: MARGIN_VIGTRN });

  // Split skuRows into 3 sections
  const MS_SKUS = ['MS-CS-CCIM', 'MS-CS-TUCM', 'MS-SE-CCEH', 'MS-CP'];
  const ST_SKUS = ['MS-SM-STM'];
  const msRows = skuRows.filter(r => MS_SKUS.includes(r.sku));
  const stRows = skuRows.filter(r => ST_SKUS.includes(r.sku));

  // Subcontractor rows (Vigilus vendor cost – internal cost-tracking, not customer-facing)
  const subRows = [];
  // Subcontractor rows — spreadsheet columns:
  //   Ext Price is not a real figure for these rows — it's already reflected in the
  //   matching Presidio Managed Services row above (K16/K17 = I16pmo/I17pmo × term),
  //   so it's intentionally not carried here (fixed in the ECCO template: the old
  //   Factors!B59*Factors!B57 / Factors!B60*Factors!B59 formulas didn't correspond
  //   to anything meaningful and have been removed from the source workbook).
  //   L30/L31 (Ext Cost) = J30/J31 × term = J16cmo/J17cmo × term
  //   Margin %/$ compare that top-field ext price (K16/K17) against this row's
  //   subcontractor ext cost (L30/L31).
  if (calc.J16cmo > 0) {
    const extCost16 = calc.J16cmo * calc.term;                 // L30
    const topEp16   = calc.I16pmo * calc.term;                 // top-field ext price (K16)
    subRows.push({ sku: 'MS-SS', desc: 'Vigilus Synthetic Transactions Setup Cloud Select Plus', billing: 'Monthly', cmo: calc.J16cmo, pmo: calc.I16pmo, topEp: topEp16, ec: extCost16, mgn: topEp16 > 0 ? (topEp16 - extCost16) / topEp16 : 0 });
  }
  if (calc.J17cmo > 0) {
    const extCost17 = calc.J17cmo * calc.term;                 // L31
    const topEp17   = calc.I17pmo * calc.term;                 // top-field ext price (K17)
    subRows.push({ sku: 'MS-SS', desc: 'Vigilus Synthetic Transactions Setup Add-On', billing: 'Monthly', cmo: calc.J17cmo, pmo: calc.I17pmo, topEp: topEp17, ec: extCost17, mgn: topEp17 > 0 ? (topEp17 - extCost17) / topEp17 : 0 });
  }

  // All rows for totals
  const allSkuRows = [...msRows, ...stRows];
  const skuTotEP = allSkuRows.reduce((s, r) => s + r.ep, 0);
  const skuTotEC = allSkuRows.reduce((s, r) => s + r.ec, 0);
  const skuTotMP = skuTotEP > 0 ? (skuTotEP - skuTotEC) / skuTotEP : 0;

  // Helper to get effective margin for a row (override or default)
  const getEffMgn = (r) => {
    const k = skuKey(r);
    return skuMarginOverrides[k] !== undefined ? skuMarginOverrides[k] / 100 : r.mgn;
  };

  // Helper to get effective price/mo for a row
  const getEffPmo = (r) => {
    const k = skuKey(r);
    if (skuMarginOverrides[k] !== undefined) {
      return r.cmo > 0 ? price(r.cmo, skuMarginOverrides[k] / 100) : 0;
    }
    return r.pmo;
  };

  // Helper to get default billing frequency for a row
  const getDefaultBilling = (r) => {
    if (r.type === 'Total In Advance') return 'Total In Advance';
    if (r.recurring) return 'Monthly';
    return 'One-Time';
  };

  // Helper to get effective billing for a row
  const getEffBilling = (r) => {
    const k = skuKey(r);
    return skuBillingOverrides[k] !== undefined ? skuBillingOverrides[k] : getDefaultBilling(r);
  };

  // SKU table row renderer
  const renderSkuRow = (r, i) => {
    const k = skuKey(r);
    const effMgn = getEffMgn(r);
    const effPmo = getEffPmo(r);
    // MS-SS subcontractor rows carry a pre-computed ep (K30/K31 formula from spreadsheet)
    // that differs from pmo×term, so use r.ep directly for those rows.
    const effEP  = r.sku === 'MS-SS' ? r.ep : (r.recurring ? effPmo * r.term : effPmo);
    const hasMarginOverride = skuMarginOverrides[k] !== undefined;
    const hasBillingOverride = skuBillingOverrides[k] !== undefined;
    return (
      <Table.Tr key={k + i}>
        <Table.Td style={{ fontFamily: 'monospace', fontSize: 11 }}>{r.sku}</Table.Td>
        <Table.Td style={{ fontSize: 11, maxWidth: 200 }}>{r.desc}</Table.Td>
        <Table.Td w={130}>
          <Select
            size="xs"
            w={120}
            data={BILLING_OPTIONS}
            value={getEffBilling(r)}
            onChange={v => {
              if (v && v !== getDefaultBilling(r)) {
                setSkuBillingOverrides(prev => ({ ...prev, [k]: v }));
              } else {
                setSkuBillingOverrides(prev => { const next = { ...prev }; delete next[k]; return next; });
              }
            }}
            styles={{ input: { fontSize: 11, color: hasBillingOverride ? 'var(--mantine-color-blue-7)' : undefined } }}
          />
        </Table.Td>
        <Table.Td ta="right">{r.term}</Table.Td>
        <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(effPmo)}</Table.Td>
        <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--mantine-color-dimmed)' }}>{r.hideVendorCost ? '—' : fmt(r.cmo)}</Table.Td>
        <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(effEP)}</Table.Td>
        <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--mantine-color-dimmed)' }}>{r.hideVendorCost ? '—' : fmt(r.ec)}</Table.Td>
        <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums', color: effEP - r.ec >= 0 ? 'var(--mantine-color-green-7)' : 'var(--mantine-color-red-7)' }}>{r.hideVendorCost ? '—' : fmt(effEP - r.ec)}</Table.Td>
        <Table.Td ta="right">
          {r.sku === 'MS-SS' ? (
            <Badge size="xs" color={effMgn >= 0.249 ? 'green' : effMgn > 0 ? 'orange' : 'red'} variant="light">
              {pct(effMgn)}
            </Badge>
          ) : (
            <Group gap={4} wrap="nowrap" justify="flex-end">
              <NumberInput
                size="xs"
                w={64}
                min={0}
                max={100}
                step={1}
                hideControls
                value={Math.round(effMgn * 100)}
                rightSection={<Text size="9px" c="dimmed">%</Text>}
                styles={{ input: { fontSize: 11, color: hasMarginOverride ? 'var(--mantine-color-blue-7)' : undefined } }}
                onChange={v => {
                  if (v === undefined || v === null || v === '') return;
                  setSkuMarginOverrides(prev => ({ ...prev, [k]: +v }));
                }}
              />
              {hasMarginOverride && (
                <Text
                  size="9px"
                  c="blue"
                  style={{ cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap' }}
                  onClick={() => setSkuMarginOverrides(prev => { const next = { ...prev }; delete next[k]; return next; })}
                >(auto)</Text>
              )}
            </Group>
          )}
        </Table.Td>
      </Table.Tr>
    );
  };

  const skuTableHead = (
    <Table.Thead>
      <Table.Tr>
        <Table.Th w={90}>SKU</Table.Th>
        <Table.Th>Service Description</Table.Th>
        <Table.Th w={130}>Billing Frequency</Table.Th>
        <Table.Th ta="right">Term</Table.Th>
        <Table.Th ta="right">Price/Mo</Table.Th>
        <Table.Th ta="right">Cost/Mo</Table.Th>
        <Table.Th ta="right">Ext Price</Table.Th>
        <Table.Th ta="right">Ext Cost</Table.Th>
        <Table.Th ta="right">Margin $</Table.Th>
        <Table.Th ta="right">Margin %</Table.Th>
      </Table.Tr>
    </Table.Thead>
  );

  const sectionHeaderRow = (label, color) => (
    <Table.Tr style={{ background: color }}>
      <Table.Td colSpan={10} fw={700} style={{ fontSize: 12 }}>{label}</Table.Td>
    </Table.Tr>
  );

  return (
    <Box style={{ minHeight: '100vh', background: '#f3f4f6' }}>
      {/* Header */}
      <Box style={{ background: 'linear-gradient(135deg,#003A8C 0%,#0057B8 60%,#1a73e8 100%)', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack gap={2}>
          <Text fw={700} c="white" size="lg">⚡ ECCO Cloud Support Pricing Calculator</Text>
          <Text size="xs" c="rgba(255,255,255,0.7)">Presidio Managed Services · Contact Center · Model v2.8 – OEF Hour-Based Formulas</Text>
        </Stack>
        <Badge variant="outline" style={{ borderColor: 'rgba(255,255,255,0.4)', color: 'white' }}>ECCO MODEL V2.8</Badge>
      </Box>

      <Container size="xl" py="md">
        <Button
          leftSection={<IconArrowLeft size={16} />}
          variant="subtle"
          color="blue"
          mb="md"
          onClick={onBackToPlatformSelector}
        >
          Back to SA Tools
        </Button>

        <Stack gap="sm">
          {/* Summary cards */}
          <Grid gutter="xs">
            {[
              { label: 'Monthly Price', value: fmt(calc.recurringPriceMo), sub: `${config.agents} agents · ${calc.totalChQty} channels`, hl: true },
              { label: 'Contract Value', value: fmt(calc.totalValue), sub: `${calc.term}-month contract` },
              { label: 'Gross Margin', value: pct(calc.marginPct), sub: fmt(calc.marginAmt) + ' margin $', color: marginColor },
              { label: 'Total Cost', value: fmt(calc.totalCost), sub: 'internal cost' },
            ].map(m => (
              <Grid.Col span={3} key={m.label}>
                <Paper withBorder p="sm" radius="md" style={{ background: m.hl ? 'var(--mantine-color-blue-7)' : 'white', height: '100%' }}>
                  <Text size="10px" fw={700} c={m.hl ? 'rgba(255,255,255,0.7)' : 'dimmed'} style={{ textTransform: 'uppercase', letterSpacing: '.5px' }}>{m.label}</Text>
                  <Text size="lg" fw={800} c={m.hl ? 'white' : m.color || 'dark'} my={2}>{m.value}</Text>
                  <Text size="10px" c={m.hl ? 'rgba(255,255,255,0.6)' : 'dimmed'}>{m.sub}</Text>
                </Paper>
              </Grid.Col>
            ))}
          </Grid>

          {/* Margin bar */}
          <Paper withBorder p="sm" radius="md">
            <Group justify="space-between" mb={6}>
              <Text size="sm" fw={700}>Margin Health</Text>
              <Badge color={marginColor} variant="light">
                {calc.marginPct >= rates.targetMargin / 100 ? '✓ On Target' : calc.marginPct >= (rates.targetMargin / 100) * 0.8 ? '⚠ Below Target' : '✗ Low Margin'} ({pct(calc.marginPct)})
              </Badge>
            </Group>
            <Progress value={Math.min(calc.marginPct * 100, 100)} color={marginColor} size="sm" />
            <Group justify="space-between" mt={3}>
              <Text size="10px" c="dimmed">0%</Text>
              <Text size="10px" c="dimmed">Target: {rates.targetMargin}%</Text>
              <Text size="10px" c="dimmed">100%</Text>
            </Group>
          </Paper>

          {/* Tabs card */}
          <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
            <Box p="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
              <Group justify="space-between" align="flex-start">
                <Box>
                  <Text fw={700} size="sm">📊 Pricing Detail</Text>
                  <Text size="xs" c="dimmed">{projectInfo.contractType} · {tier}</Text>
                </Box>
                <Group gap="xs">
                  <Button size="xs" variant="subtle" leftSection={<IconRefresh size={12} />} onClick={resetForm}>Reset</Button>
                  <Button size="xs" variant="subtle" color="teal" leftSection={<IconDownload size={12} />} onClick={exportJSON}>JSON</Button>
                  <Button size="xs" variant="filled" color="green" leftSection={<IconDownload size={12} />} onClick={exportToExcel} loading={xlsxExporting}>Excel 2.8</Button>
                  <Tooltip label={projectInfo.oppNum ? `Save to SharePoint (Opp: ${projectInfo.oppNum})` : 'Enter an Opportunity # to enable SharePoint save'} withArrow>
                    <Button
                      size="xs"
                      variant={projectInfo.oppNum ? 'filled' : 'light'}
                      color={projectInfo.oppNum ? 'green' : 'gray'}
                      leftSection={<IconCloudUpload size={12} />}
                      style={{ cursor: projectInfo.oppNum ? 'pointer' : 'not-allowed', opacity: projectInfo.oppNum ? 1 : 0.55 }}
                      onClick={() => {}}
                    >
                      Save to SharePoint
                    </Button>
                  </Tooltip>
                  <Button size="xs" variant="light" color="green" leftSection={<IconUpload size={12} />} onClick={() => xlsxImportRef.current?.click()}>Import Excel</Button>
                  <input ref={xlsxImportRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={importFromExcel} />
                  <Button size="xs" variant="subtle" color="blue" leftSection={<IconUpload size={12} />} onClick={() => importRef.current?.click()}>Import JSON</Button>
                  <input ref={importRef} type="file" accept=".json" style={{ display: 'none' }} onChange={importJSON} />
                  <Button size="xs" variant="filled" color="blue" leftSection={<IconMail size={12} />} onClick={() => window.print()}>Print</Button>
                </Group>
              </Group>
            </Box>

            <Tabs value={activeTab} onChange={setActiveTab}>
              <Tabs.List px="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                {['factors', 'oef', 'channels', 'rates', 'quote', 'vigilus'].map(t => (
                  <Tabs.Tab key={t} value={t} fz="xs">
                    {t === 'factors' ? 'Factors' : t === 'oef' ? 'CC OEF' : t === 'channels' ? 'Channel Details' : t === 'rates' ? 'Rate Sheet' : t === 'quote' ? 'Quote Summary' : 'Vigilus RFQ'}
                  </Tabs.Tab>
                ))}
              </Tabs.List>

              {/* Factors Tab */}
              <Tabs.Panel value="factors" p="md">
                <Stack gap="sm">

                  {/* Project Info */}
                  <Paper withBorder p="md" radius="md">
                    <Text fw={700} size="sm" mb="sm">📋 Project Information</Text>
                    <Stack gap="xs">
                      <TextInput label="Customer Name" placeholder="e.g. Borant Corporation" size="xs" value={projectInfo.customerName} onChange={e => pi('customerName', e.target.value)} />
                      <Grid gutter="xs">
                        <Grid.Col span={6}><TextInput label="Opportunity #" placeholder="OPP-XXXXX" size="xs" value={projectInfo.oppNum} onChange={e => pi('oppNum', e.target.value)} /></Grid.Col>
                        <Grid.Col span={6}><TextInput label="AM Name" size="xs" value={projectInfo.amName} onChange={e => pi('amName', e.target.value)} /></Grid.Col>
                        <Grid.Col span={6}><Select label="SA Name" size="xs" placeholder="– Select SA –" data={SA_NAMES} value={projectInfo.saName || null} onChange={v => pi('saName', v || '')} clearable /></Grid.Col>
                        <Grid.Col span={6}><Select label="BDM Name" size="xs" placeholder="– Select BDM –" data={BDM_NAMES} value={projectInfo.bdmName || null} onChange={v => pi('bdmName', v || '')} clearable /></Grid.Col>
                        <Grid.Col span={6}><Select label="Platform" size="xs" data={PLATFORMS} value={projectInfo.platform} onChange={v => pi('platform', v || 'WxCC')} /></Grid.Col>
                        <Grid.Col span={6}><Select label="Contract Type" size="xs" data={CONTRACT_TYPES} value={projectInfo.contractType} onChange={v => pi('contractType', v || 'New')} /></Grid.Col>
                        <Grid.Col span={12}><NumberInput label="Contract Term (Months)" size="xs" min={1} max={60} value={projectInfo.contractTerm} onChange={v => pi('contractTerm', +v || 12)} /></Grid.Col>
                      </Grid>
                    </Stack>
                  </Paper>

                  {/* Support Tier */}
                  <Paper withBorder p="md" radius="md">
                    <Text fw={700} size="sm" mb="xs">🛡️ Contact Center Support Type</Text>
                    <Group gap="xs" grow mb="md">
                      {TIERS.map(t => <SelectBtn key={t} label={t} sub={TIER_DESC[t]} active={tier === t} onClick={() => setTier(t)} />)}
                    </Group>

                    <Divider label="Agent & Channel Configuration" labelPosition="center" mb="xs" />
                    <Grid gutter="xs">
                      <Grid.Col span={6}><NumberInput label="Licensed Agents / Supervisors" size="xs" min={0} hideControls value={config.agents} onChange={v => cfg('agents', +v || 0)} description="Hour multiplier formulas" /></Grid.Col>
                      <Grid.Col span={6}><NumberInput label="AI Assistants" size="xs" min={0} hideControls value={config.ai} onChange={v => cfg('ai', +v || 0)} description="Min 1 for uncommitted" /></Grid.Col>
                      <Grid.Col span={6}><NumberInput label="Email EntryPoints" size="xs" min={0} hideControls value={config.emailEP} onChange={v => cfg('emailEP', +v || 0)} description="Visibility only" /></Grid.Col>
                      <Grid.Col span={6}><NumberInput label="Chat/Social EntryPoints" size="xs" min={0} hideControls value={config.chatEP} onChange={v => cfg('chatEP', +v || 0)} description="Visibility only" /></Grid.Col>
                      <Grid.Col span={6}>
                        <NumberInput
                          label={<>IVR Ports {config.ivrPortsManual && <Badge size="xs" color="orange" variant="light" ml={4} style={{ cursor: 'pointer' }} onClick={() => setConfig(p => ({ ...p, ivrPortsManual: false, ivrPorts: p.agents }))}>manual – reset</Badge>}</>}
                          size="xs" min={0} hideControls
                          value={config.ivrPorts}
                          onChange={v => setConfig(p => ({ ...p, ivrPorts: +v || 0, ivrPortsManual: (+v || 0) !== p.agents }))}
                          description="Syncs to agent count; edit to override"
                        />
                      </Grid.Col>
                      <Grid.Col span={6}><NumberInput label="Dialer Ports" size="xs" min={0} hideControls value={config.dialerPorts} onChange={v => cfg('dialerPorts', +v || 0)} description="BF needs Webex Calling · MACD needs Cloud Select" /></Grid.Col>
                      <Grid.Col span={6}><NumberInput label="Third Party Apps" size="xs" min={0} hideControls value={config.thirdParty} onChange={v => cfg('thirdParty', +v || 0)} description="Calabrio, Nuance, etc." /></Grid.Col>
                      <Grid.Col span={6}><TextInput label="Third Party Notes" size="xs" placeholder="e.g. QM" value={config.thirdPartyNotes} onChange={e => cfg('thirdPartyNotes', e.target.value)} /></Grid.Col>
                      <Grid.Col span={12}><NumberInput label="Customer Provided Ticket Count" size="xs" min={0} hideControls value={config.custTickets} onChange={v => cfg('custTickets', +v || 0)} description="Per term – overrides estimate if set" /></Grid.Col>
                    </Grid>

                    <Divider label="Webex Calling" labelPosition="center" my="xs" />
                    <Group gap="xs" grow>
                      {WX_OPTIONS.map(w => (
                        <SelectBtn key={w} label={w} active={wx === w} onClick={() => setWx(w)}
                          sub={w === 'Cloud Select' ? 'Cost $3.22 · Price $4.29/agent' : w === 'Cloud Reactive' ? 'Cost $1.95 · Price $2.60/agent' : 'Not included'}
                        />
                      ))}
                    </Group>
                  </Paper>

                  {/* Agentless Channels */}
                  <Paper withBorder p="md" radius="md">
                    <Group justify="space-between" mb="sm">
                      <Text fw={700} size="sm">🤖 AI Agent / Agentless Channels</Text>
                      <Button size="xs" variant="light" leftSection={<IconPlus size={12} />} onClick={addChannel}>Add Channel</Button>
                    </Group>
                    {channels.length === 0 && <Text size="xs" c="dimmed" ta="center" py="sm">No channels yet – click Add Channel to start.</Text>}
                    <Accordion variant="separated" chevronSize={14}>
                      {channels.map(ch => {
                        const qty = parseFloat(ch.qty) || 1;
                        const hpu = ch.category === 'Advanced' ? HRS_ADV : HRS_BASIC;
                        const pMo = price(hpu * qty * calc.dcEng, MARGIN_STD);
                        return (
                          <Accordion.Item key={ch.id} value={String(ch.id)}>
                            <Accordion.Control>
                              <Group gap={6} wrap="nowrap">
                                <Badge size="xs" color="blue" variant="filled" style={{ flexShrink: 0 }}>{ch.id}</Badge>
                                <Text size="xs" fw={600} style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ch.name}</Text>
                                <Badge size="xs" color={ch.category === 'Advanced' ? 'orange' : 'blue'} variant="light">{ch.category}</Badge>
                                <Badge size="xs" color="green" variant="light">{fmt(pMo)}/mo</Badge>
                              </Group>
                            </Accordion.Control>
                            <Accordion.Panel>
                              <Stack gap="xs">
                                <Grid gutter="xs">
                                  <Grid.Col span={8}><TextInput label="Channel Name" size="xs" value={ch.name} onChange={e => updCh(ch.id, 'name', e.target.value)} /></Grid.Col>
                                  <Grid.Col span={4}><NumberInput label="Qty" size="xs" min={1} hideControls value={ch.qty} onChange={v => updCh(ch.id, 'qty', +v || 1)} /></Grid.Col>
                                </Grid>
                                <Box>
                                  <Text size="10px" fw={700} c="dimmed" mb={4} style={{ textTransform: 'uppercase', letterSpacing: '.4px' }}>Channel Types</Text>
                                  <Group gap={4}>
                                    {['Voice', 'SMS', 'Chat', 'Email', 'Social', 'Dialer'].map(t => (
                                      <Box key={t} onClick={() => toggleType(ch.id, t)} style={{ border: `1.5px solid ${ch.types.includes(t) ? 'var(--mantine-color-teal-6)' : 'var(--mantine-color-gray-3)'}`, borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: ch.types.includes(t) ? 'var(--mantine-color-teal-7)' : 'var(--mantine-color-gray-6)', background: ch.types.includes(t) ? 'var(--mantine-color-teal-0)' : 'white' }}>{t}</Box>
                                    ))}
                                  </Group>
                                </Box>
                                <Box>
                                  <Text size="10px" fw={700} c="dimmed" mb={4} style={{ textTransform: 'uppercase', letterSpacing: '.4px' }}>Category</Text>
                                  <Group gap="xs" grow>
                                    {['Advanced', 'Basic'].map(cat => (
                                      <Box key={cat} onClick={() => updCh(ch.id, 'category', cat)} style={{ border: `2px solid ${ch.category === cat ? 'var(--mantine-color-blue-6)' : 'var(--mantine-color-gray-3)'}`, borderRadius: 6, padding: '6px', cursor: 'pointer', background: ch.category === cat ? 'var(--mantine-color-blue-0)' : 'white', textAlign: 'center' }}>
                                        <Text size="xs" fw={700} c={ch.category === cat ? 'blue' : 'dark'}>{cat} – {cat === 'Advanced' ? '$257.50' : '$125.00'}/mo</Text>
                                        <Text size="10px" c="dimmed">{cat === 'Advanced' ? 'IVR/SMS/Dialer + custom API' : 'FAQ bot, standard connectors'}</Text>
                                      </Box>
                                    ))}
                                  </Group>
                                </Box>
                                <Grid gutter="xs">
                                  <Grid.Col span={6}><Select label="Platform" size="xs" placeholder="AI vendor" data={AI_PLATFORMS} value={ch.platform} onChange={v => updCh(ch.id, 'platform', v || '')} /></Grid.Col>
                                  <Grid.Col span={6}><Select label="Deployment" size="xs" data={['Cloud', 'On-Premise', 'Hybrid']} value={ch.deployment} onChange={v => updCh(ch.id, 'deployment', v || 'Cloud')} /></Grid.Col>
                                  <Grid.Col span={12}><TextInput label="Integration Platform" size="xs" placeholder="e.g. CCaaS, CRM" value={ch.integration} onChange={e => updCh(ch.id, 'integration', e.target.value)} /></Grid.Col>
                                </Grid>
                                <Textarea label="Business Objective" size="xs" autosize minRows={2} value={ch.businessObjective} onChange={e => updCh(ch.id, 'businessObjective', e.target.value)} />
                                <Textarea label="Target Use Cases" size="xs" autosize minRows={2} value={ch.useCases} onChange={e => updCh(ch.id, 'useCases', e.target.value)} />
                                <Button size="xs" color="red" variant="light" leftSection={<IconTrash size={12} />} onClick={() => removeChannel(ch.id)}>Remove</Button>
                              </Stack>
                            </Accordion.Panel>
                          </Accordion.Item>
                        );
                      })}
                    </Accordion>
                  </Paper>

                  {/* Add-Ons */}
                  <Paper withBorder p="md" radius="md">
                    <Text fw={700} size="sm" mb="sm">⚙️ Add-On & One-Time Services</Text>
                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Box>
                          <Text size="xs" fw={600}>Cloud Onboarding (PMN)</Text>
                          <Text size="10px" c="dimmed">Auto-applies for New contracts · WxCC/Twilio/Amazon = ${rates.onboardingFeeStd || 500} · Five9/Genesys = ${rates.onboardingFeePrem || 800} · 40% margin</Text>
                        </Box>
                        <Badge size="sm" color={projectInfo.contractType === 'New' ? 'teal' : 'gray'} variant="light">
                          {projectInfo.contractType === 'New' ? 'Applied' : 'N/A'}
                        </Badge>
                      </Group>
                      <Divider />
                      <Group justify="space-between">
                        <Box>
                          <Text size="xs" fw={600}>Vigilus ServiceNow Integration</Text>
                          <Text size="10px" c="dimmed">Auto-applies for Cloud Select Plus or any synthetic minutes add-on · $1,250 price · 20% margin</Text>
                        </Box>
                        <Badge size="sm" color={calc.vigilusServiceNow ? 'teal' : 'gray'} variant="light">
                          {calc.vigilusServiceNow ? 'Applied' : 'N/A'}
                        </Badge>
                      </Group>
                      <Divider label="Elective & Synthetic" labelPosition="center" />
                      <Grid gutter="xs">
                        <Grid.Col span={6}><NumberInput label="Elective Service Hours" size="xs" min={0} hideControls value={addOns.electiveHours} onChange={v => ao('electiveHours', +v || 0)} description="$195/hr ESR Rate" /></Grid.Col>
                        <Grid.Col span={6}><NumberInput label="Vigilus Training Sessions" size="xs" min={0} hideControls value={addOns.vigilusSessions} onChange={v => ao('vigilusSessions', +v || 0)} description={`$${rates.vigilusTrainingPerSession || 200}/session · 20% margin`} /></Grid.Col>
                        <Grid.Col span={6}><NumberInput label="Synthetic Numbers (Add-On)" size="xs" min={0} hideControls value={addOns.synthCount} onChange={v => ao('synthCount', +v || 0)} description={`$${rates.synthSetupFeeAddon || 500}/number setup + $${rates.synthAnnualCostAddon || 1000}/yr recurring`} /></Grid.Col>
                        <Grid.Col span={6}><NumberInput label="Annual Minutes ×10,000" size="xs" min={0} hideControls value={addOns.annualMinutes} onChange={v => ao('annualMinutes', +v || 0)} description="Label only · triggers ServiceNow fee" /></Grid.Col>
                      </Grid>
                    </Stack>
                  </Paper>

                </Stack>
              </Tabs.Panel>

              {/* OEF Tab */}
              <Tabs.Panel value="oef" p="md">
                <Stack gap="xs" mb="sm">
                  {config.agents === 0 && calc.totalChQty === 0 && <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={14} />} p="xs"><Text size="xs">No agents or channels configured yet. Add them in the Factors tab.</Text></Alert>}
                  {tier === 'Cloud Select Plus' && config.ai === 0 && <Alert color="blue" variant="light" icon={<IconInfoCircle size={14} />} p="xs"><Text size="xs">Cloud Select Plus: Minimum 1 AI Assistant required for uncommitted pricing.</Text></Alert>}
                  {calc.marginPct > 0 && calc.marginPct < 0.20 && <Alert color="red" variant="light" icon={<IconAlertTriangle size={14} />} p="xs"><Text size="xs">🚨 Margin critically low ({pct(calc.marginPct)}). Review before submitting.</Text></Alert>}
                  {calc.totalValue > 100000 && <Alert color="orange" variant="light" icon={<IconAlertTriangle size={14} />} p="xs"><Text size="xs">⚠️ Contract value &gt;$100,000. Milestone revision required per ECCO policy.</Text></Alert>}
                </Stack>
                <Alert color="blue" variant="light" p="xs" mb="sm">
                  <Text size="xs" fw={700}>Active Tier: {tier}</Text>
                  <Text size="xs">Cost/Mo = Total Hours × ${rates.dcEngCost} DC Eng Rate ÷ {calc.term} months · Price/Mo = Cost ÷ 0.75 (25% margin)</Text>
                </Alert>
                <ScrollArea>
                  <Table fz="xs" striped withTableBorder withColumnBorders>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th w={30}>#</Table.Th>
                        <Table.Th>Support Services Description</Table.Th>
                        <Table.Th ta="right">B/F Hrs</Table.Th>
                        <Table.Th ta="right">MACD Hrs</Table.Th>
                        <Table.Th ta="right">Cost/Mo</Table.Th>
                        <Table.Th ta="right">Cost/Yr</Table.Th>
                        <Table.Th ta="right">Price/Yr</Table.Th>
                        <Table.Th ta="right">B/F Ticket</Table.Th>
                        <Table.Th ta="right">MACD Ticket</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {oefRows.map(r => {
                        // Spreadsheet hardcodes SDM (row #7) ticket columns to 0 — it doesn't
                        // derive them from hours — so we match that behaviour here.
                        const isSdm   = r.n === 7;
                        const bfTkt   = (!isSdm && r.bf   > 0) ? r.bf   / 2 : 0;
                        const macdTkt = (!isSdm && r.macd > 0) ? r.macd / 1 : 0;
                        return (
                          <Table.Tr key={r.n}>
                            <Table.Td>{r.n}</Table.Td>
                            <Table.Td style={{ fontSize: 11 }}>{r.desc}</Table.Td>
                            <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{r.bf > 0 ? fmtN(r.bf, 2) : r.cmo > 0 ? '—' : '0'}</Table.Td>
                            <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{r.macd > 0 ? fmtN(r.macd, 2) : r.cmo > 0 ? '—' : '0'}</Table.Td>
                            <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtD(r.cmo)}</Table.Td>
                            <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtD(r.cmo * 12)}</Table.Td>
                            <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtD(r.pmo * 12)}</Table.Td>
                            <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--mantine-color-dimmed)' }}>{bfTkt > 0 ? fmtN(bfTkt, 2) : '0'}</Table.Td>
                            <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--mantine-color-dimmed)' }}>{macdTkt > 0 ? fmtN(macdTkt, 2) : '0'}</Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                    <Table.Tfoot>
                      <Table.Tr style={{ background: 'var(--mantine-color-gray-0)' }}>
                        <Table.Td colSpan={7} ta="right" fw={700} c="dimmed" style={{ fontSize: 11 }}>
                          Estimated Monthly Ticket Count
                        </Table.Td>
                        <Table.Td colSpan={2} ta="center" fw={700}>
                          {calc.estMonthlyTickets}{' '}
                          <Text component="span" size="10px" c="dimmed" fw={400}>({fmtN(calc.estMonthlyTicketsRaw, 2)})</Text>
                        </Table.Td>
                      </Table.Tr>
                      <Table.Tr style={{ background: 'var(--mantine-color-gray-0)' }}>
                        <Table.Td colSpan={7} ta="right" c="dimmed" style={{ fontSize: 11 }}>
                          Est B/F % / Est MACD %{config.ticketSplitManual && <Text component="span" size="10px" c="blue" ml={6} style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => cfg('ticketSplitManual', false)}>(reset to auto)</Text>}
                        </Table.Td>
                        <Table.Td colSpan={2}>
                          <Group gap={4} justify="center" wrap="nowrap">
                            <NumberInput
                              size="xs" w={68} min={0} max={100} step={1} hideControls
                              value={config.ticketSplitManual ? config.bfPctManual : Math.round(calc.autoBFPct * 100)}
                              rightSection={<Text size="9px" c="dimmed">%BF</Text>}
                              styles={{ input: { textAlign: 'right', color: config.ticketSplitManual ? 'var(--mantine-color-blue-7)' : undefined } }}
                              onChange={v => { cfg('ticketSplitManual', true); cfg('bfPctManual', +v || 0); }}
                              onFocus={() => cfg('ticketSplitManual', true)}
                            />
                            <NumberInput
                              size="xs" w={76} min={0} max={100} step={1} hideControls
                              value={config.ticketSplitManual ? (100 - config.bfPctManual) : Math.round(calc.autoMACDPct * 100)}
                              rightSection={<Text size="9px" c="dimmed">%MACD</Text>}
                              styles={{ input: { textAlign: 'right', color: config.ticketSplitManual ? 'var(--mantine-color-blue-7)' : undefined } }}
                              onChange={v => { cfg('ticketSplitManual', true); cfg('bfPctManual', 100 - (+v || 0)); }}
                              onFocus={() => cfg('ticketSplitManual', true)}
                            />
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    </Table.Tfoot>
                  </Table>
                </ScrollArea>

                <Divider label="Presidio Managed Services – SKU Summary" labelPosition="center" my="md" />
                <ScrollArea>
                  <Table fz="xs" striped withTableBorder withColumnBorders>
                    {skuTableHead}
                    <Table.Tbody>
                      {sectionHeaderRow('Presidio Managed Services', 'var(--mantine-color-blue-0)')}
                      {msRows.map((r, i) => renderSkuRow(r, i))}
                      {sectionHeaderRow('Service Transition', 'var(--mantine-color-teal-0)')}
                      {stRows.map((r, i) => renderSkuRow(r, i))}
                      {sectionHeaderRow('Subcontractor (Direct Vendor Costs)', 'var(--mantine-color-orange-0)')}
                      {subRows.map((r, i) => {
                        const marginDollar = r.topEp - r.ec;
                        const marginPct    = r.mgn;
                        return (
                          <Table.Tr key={skuKey(r) + i}>
                            <Table.Td style={{ fontFamily: 'monospace', fontSize: 11 }}>{r.sku}</Table.Td>
                            <Table.Td style={{ fontSize: 11, maxWidth: 200 }}>{r.desc}</Table.Td>
                            <Table.Td><Text size="xs" c="dimmed">{r.billing}</Text></Table.Td>
                            <Table.Td ta="right">—</Table.Td>
                            <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>—</Table.Td>
                            <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtD(r.cmo)}</Table.Td>
                            <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>—</Table.Td>
                            <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtD(r.ec)}</Table.Td>
                            <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums', color: marginDollar >= 0 ? 'var(--mantine-color-green-7)' : 'var(--mantine-color-red-7)' }}>{fmt(marginDollar)}</Table.Td>
                            <Table.Td ta="right"><Badge size="xs" color={marginPct >= 0.249 ? 'green' : marginPct > 0 ? 'orange' : 'red'} variant="light">{pct(marginPct)}</Badge></Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                    <Table.Tfoot>
                      <Table.Tr>
                        <Table.Td colSpan={6} fw={700}>TOTAL</Table.Td>
                        <Table.Td ta="right" fw={700}>{fmt(skuTotEP)}</Table.Td>
                        <Table.Td ta="right" fw={700}>{fmt(skuTotEC)}</Table.Td>
                        <Table.Td ta="right" fw={700} style={{ color: skuTotEP - skuTotEC >= 0 ? 'var(--mantine-color-green-7)' : 'var(--mantine-color-red-7)' }}>{fmt(skuTotEP - skuTotEC)}</Table.Td>
                        <Table.Td ta="right"><Badge size="xs" color={skuTotMP >= 0.249 ? 'green' : 'orange'} variant="light">{pct(skuTotMP)}</Badge></Table.Td>
                      </Table.Tr>
                    </Table.Tfoot>
                  </Table>
                </ScrollArea>
              </Tabs.Panel>

              {/* Channel Details */}
              <Tabs.Panel value="channels" p="md">
                {channels.length === 0 ? (
                  <Alert color="blue" variant="light" icon={<IconInfoCircle size={14} />}><Text size="xs">No agentless channels configured. Add channels using the Factors tab.</Text></Alert>
                ) : (
                  <Stack gap="sm">
                    <Alert color="blue" variant="light" p="xs">
                      <Text size="xs"><strong>{channels.length} channel(s)</strong> · Adv: {calc.advQty} · Basic: {calc.basQty} · Total Qty: {calc.totalChQty}</Text>
                    </Alert>
                    <ScrollArea>
                      <Table fz="xs" striped withTableBorder withColumnBorders>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>#</Table.Th><Table.Th>Name</Table.Th><Table.Th>Types</Table.Th>
                            <Table.Th>Category</Table.Th><Table.Th ta="right">Qty</Table.Th>
                            <Table.Th ta="right">Hrs/Unit</Table.Th><Table.Th ta="right">Cost/Mo</Table.Th>
                            <Table.Th ta="right">Price/Mo</Table.Th><Table.Th ta="right">Ann. Price</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {channels.map(ch => {
                            const qty = parseFloat(ch.qty) || 1;
                            const hpu = ch.category === 'Advanced' ? HRS_ADV : HRS_BASIC;
                            const cMo = hpu * qty * rates.dcEngCost;
                            const pMo = price(cMo, MARGIN_STD);
                            return (
                              <Table.Tr key={ch.id}>
                                <Table.Td>{ch.id}</Table.Td>
                                <Table.Td fw={600}>{ch.name}</Table.Td>
                                <Table.Td><Group gap={2}>{(ch.types || ['Voice']).map(t => <Badge key={t} size="xs" color="teal" variant="light">{t}</Badge>)}</Group></Table.Td>
                                <Table.Td><Badge size="xs" color={ch.category === 'Advanced' ? 'orange' : 'blue'} variant="light">{ch.category}</Badge></Table.Td>
                                <Table.Td ta="right">{qty}</Table.Td>
                                <Table.Td ta="right">{fmtN(hpu)}</Table.Td>
                                <Table.Td ta="right">{fmt(cMo)}</Table.Td>
                                <Table.Td ta="right">{fmt(pMo)}</Table.Td>
                                <Table.Td ta="right">{fmt(pMo * calc.term)}</Table.Td>
                              </Table.Tr>
                            );
                          })}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea>
                    {channels.map(ch => (
                      <Paper key={ch.id} withBorder p="sm" radius="sm">
                        <Text size="xs" fw={700} mb="xs">{ch.name} – Channel Detail</Text>
                        <Text size="10px" fw={700} c="dimmed">PLATFORM</Text>
                        {ch.platform
                          ? <Text size="xs" mb="xs">{ch.platform}</Text>
                          : <Text size="xs" mb="xs" c="red" fw={600}>NOT FILLED OUT YET</Text>}
                        <Text size="10px" fw={700} c="dimmed">BUSINESS OBJECTIVE</Text>
                        {ch.businessObjective
                          ? <Text size="xs" mb="xs">{ch.businessObjective}</Text>
                          : <Text size="xs" mb="xs" c="red" fw={600}>NOT FILLED OUT YET</Text>}
                        <Text size="10px" fw={700} c="dimmed">TARGET USE CASES</Text>
                        {ch.useCases
                          ? <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>{ch.useCases}</Text>
                          : <Text size="xs" c="red" fw={600}>NOT FILLED OUT YET</Text>}
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Tabs.Panel>

              {/* Rate Sheet */}
              <Tabs.Panel value="rates" p="md">
                <ScrollArea>
                  <Table fz="xs" withTableBorder withColumnBorders style={{ tableLayout: 'fixed' }}>
                    <Table.Thead>
                      <Table.Tr style={{ background: 'var(--mantine-color-blue-0)' }}>
                        <Table.Th colSpan={3} fw={700} c="blue">Labor Rates</Table.Th>
                      </Table.Tr>
                      <Table.Tr style={{ background: 'var(--mantine-color-gray-0)' }}>
                        <Table.Th>Description</Table.Th><Table.Th ta="right" w={120}>Value</Table.Th><Table.Th w={50}>Unit</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {[
                        { label: 'CC Delivery Center Eng Cost', field: 'dcEngCost',    unit: '/hr' },
                        { label: 'ESR Rate',                    field: 'esrRate',      unit: '/hr' },
                        { label: 'CC Engineer / UC',            field: 'engCost',      unit: '/hr' },
                        { label: 'Target Margin',               field: 'targetMargin', unit: '%'   },
                      ].map(({ label, field, unit }) => (
                        <Table.Tr key={field}>
                          <Table.Td c="dimmed">{label}</Table.Td>
                          <Table.Td ta="right">
                            <NumberInput size="xs" min={0} hideControls value={rates[field]} styles={{ input: { textAlign: 'right' } }} onChange={v => rt(field, +v || 0)} />
                          </Table.Td>
                          <Table.Td c="dimmed">{unit}</Table.Td>
                        </Table.Tr>
                      ))}
                      <Table.Tr><Table.Td c="dimmed">B/F Hrs / ticket</Table.Td><Table.Td ta="right" fw={700} c="blue">2</Table.Td><Table.Td c="dimmed">hrs</Table.Td></Table.Tr>
                      <Table.Tr><Table.Td c="dimmed">MACD Hrs / ticket</Table.Td><Table.Td ta="right" fw={700} c="blue">1</Table.Td><Table.Td c="dimmed">hrs</Table.Td></Table.Tr>

                      <Table.Tr style={{ background: 'var(--mantine-color-teal-0)' }}>
                        <Table.Th colSpan={3} fw={700} c="teal">Add-On &amp; One-Time Pricing (Factors B45–B53)</Table.Th>
                      </Table.Tr>
                      {[
                        { label: 'Synthetic Transactions Setup Fee',             field: 'synthSetupFee',            unit: '$' },
                        { label: 'Synthetic Transactions Setup Fee Add-On',      field: 'synthSetupFeeAddon',       unit: '$' },
                        { label: 'Synthetic Transactions Cost (Annual)',         field: 'synthAnnualCost',          unit: '$' },
                        { label: 'Synthetic Transactions Cost Add-On (Annual)',  field: 'synthAnnualCostAddon',     unit: '$' },
                        { label: 'Cloud Onboarding – Standard',                  field: 'onboardingFeeStd',         unit: '$' },
                        { label: 'Cloud Onboarding – Premium (Five9/Genesys)',   field: 'onboardingFeePrem',        unit: '$' },
                        { label: 'Vigilus ServiceNow Integration (New)',         field: 'vigilusServiceNowCost',    unit: '$' },
                        { label: 'Vigilus Training per Session',                 field: 'vigilusTrainingPerSession',unit: '$' },
                        { label: 'Synthetic Minutes Annual (Included)',          field: 'synthMinsIncluded',        unit: 'min' },
                        { label: 'Synthetic Minutes Annual (Per Add-On)',        field: 'synthMinsPerAddon',        unit: 'min' },
                      ].map(({ label, field, unit }) => (
                        <Table.Tr key={field}>
                          <Table.Td c="dimmed">{label}</Table.Td>
                          <Table.Td ta="right">
                            <NumberInput size="xs" min={0} hideControls value={rates[field] ?? 0} styles={{ input: { textAlign: 'right' } }} onChange={v => rt(field, +v || 0)} />
                          </Table.Td>
                          <Table.Td c="dimmed">{unit}</Table.Td>
                        </Table.Tr>
                      ))}

                      <Table.Tr
                        style={{ background: 'var(--mantine-color-violet-0)', cursor: 'pointer' }}
                        onClick={() => setOefMultipliersOpen(o => !o)}
                      >
                        <Table.Th colSpan={3} fw={700} c="violet">
                          OEF Hour Multipliers by Tier {oefMultipliersOpen ? '▲' : '▼'}
                        </Table.Th>
                      </Table.Tr>
                    </Table.Tbody>
                  </Table>

                  {oefMultipliersOpen && (
                    <Table fz="xs" striped withTableBorder withColumnBorders>
                      <Table.Thead><Table.Tr><Table.Th>Component</Table.Th><Table.Th>Select Plus</Table.Th><Table.Th>Select</Table.Th><Table.Th>Reactive</Table.Th></Table.Tr></Table.Thead>
                      <Table.Tbody>
                        {[
                          ['Agent B/F',    '0.04×A×T',  '0.04×A×T',  '0.05×A×T'],
                          ['AI B/F',       '0.02×AI×T', '0.02×AI×T', '0.025×AI×T'],
                          ['Agent MACD',   '0.06×A×T',  '0.06×A×T',  '—'],
                          ['AI MACD',      '0.03×AI×T', '0.03×AI×T', '—'],
                          ['Scripting B/F','0.008×IVR×T+0.004×AI×T','same','—'],
                          ['Scripting MACD','0.016×IVR×T+0.008×AI×T','same','—'],
                          ['SDM B/F',      '0.002×A×T+0.001×AI×T','same','—'],
                          ['SDM MACD',     '0.004×A×T+0.002×AI×T','same','—'],
                          ['Adv Agentless','2.575 hrs/ch/T','',''],
                          ['Basic Agentless','1.25 hrs/ch/T','',''],
                          ['3rd Party App','1.87 hrs/app/T','',''],
                          ['Dialer B/F',   '0.00625×D×T if Webex≠None','',''],
                          ['Dialer MACD',  '0.01×D×T (Cloud Select only)','',''],
                        ].map(row => (
                          <Table.Tr key={row[0]}>{row.map((cell, i) => <Table.Td key={i} colSpan={i === 1 && !row[2] ? 3 : 1} style={{ fontSize: 10 }}>{cell}</Table.Td>)}</Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  )}
                </ScrollArea>
              </Tabs.Panel>

              {/* Quote Summary */}
              <Tabs.Panel value="quote" p="md">
                <Alert color="blue" variant="light" p="sm" mb="md" icon={<IconInfoCircle size={14} />}>
                  <Text size="sm" fw={700}>Quote Summary – {projectInfo.customerName || '[Customer]'}</Text>
                  <Text size="xs">Opp: {projectInfo.oppNum || '—'} · AM: {projectInfo.amName || '—'} · BDM: {projectInfo.bdmName || '—'} · SA: {projectInfo.saName || '—'} · Platform: {projectInfo.platform}</Text>
                </Alert>
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Table fz="xs" withTableBorder>
                      <Table.Thead><Table.Tr><Table.Th>Field</Table.Th><Table.Th>Value</Table.Th></Table.Tr></Table.Thead>
                      <Table.Tbody>
                        {[
                          ['Customer',         <Text fw={700}>{projectInfo.customerName || '—'}</Text>],
                          ['Platform',         projectInfo.platform],
                          ['Support Tier',     <Badge size="xs" color="blue">{tier}</Badge>],
                          ['Contract Type',    <Badge size="xs" color="teal">{projectInfo.contractType}</Badge>],
                          ['AM',               projectInfo.amName || '—'],
                          ['SA',               projectInfo.saName || '—'],
                          ['BDM',              projectInfo.bdmName || '—'],
                          ['Contract Term',    `${calc.term} months`],
                          ['Agents',           config.agents],
                          ['AI Assistants',    config.ai],
                          ['IVR Ports',        calc.ivrPorts],
                          ['Adv Channels',     `${calc.advQty} (${channels.filter(c => c.category === 'Advanced').length} defined)`],
                          ['Basic Channels',   `${calc.basQty} (${channels.filter(c => c.category === 'Basic').length} defined)`],
                          ['Third Party Apps', config.thirdParty],
                          ['Webex Calling',    wx === 'None' ? 'None' : `${wx} – $${wx === 'Reactive' ? '2.60' : '4.29'}/agent/mo`],
                        ].map(([k, v]) => <Table.Tr key={k}><Table.Td c="dimmed">{k}</Table.Td><Table.Td>{v}</Table.Td></Table.Tr>)}
                      </Table.Tbody>
                    </Table>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Table fz="xs" withTableBorder>
                      <Table.Thead><Table.Tr><Table.Th>Financial</Table.Th><Table.Th>Value</Table.Th></Table.Tr></Table.Thead>
                      <Table.Tbody>
                        {[
                          ['Monthly Recurring Price', <Text fw={700}>{fmt(calc.recurringPriceMo)}/mo</Text>],
                          ['Monthly Recurring Cost',  `${fmt(calc.recurringCostMo)}/mo`],
                          ['Monthly Margin',          `${fmt(calc.recurringPriceMo - calc.recurringCostMo)}/mo`],
                          ['One-Time Fees Price',     fmt(calc.oneTimePrice)],
                          ['One-Time Fees Cost',      fmt(calc.oneTimeCost)],
                          ['Total Contract Value',    <Text fw={700} c="blue">{fmt(calc.totalValue)}</Text>],
                          ['Total Contract Cost',     fmt(calc.totalCost)],
                          ['Gross Margin $',          <Text fw={700} c="green">{fmt(calc.marginAmt)}</Text>],
                          ['Gross Margin %',          <Badge size="xs" color={marginColor}>{pct(calc.marginPct)}</Badge>],
                          ['Date',                    new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })],
                        ].map(([k, v]) => <Table.Tr key={k}><Table.Td c="dimmed">{k}</Table.Td><Table.Td>{v}</Table.Td></Table.Tr>)}
                      </Table.Tbody>
                    </Table>
                  </Grid.Col>
                </Grid>
                <Alert color="orange" variant="light" mt="md" p="xs">
                  <Text size="xs">📋 Quote all Setup Fees, Elective Services, and non-Contact Center Services separately. Milestone threshold: $50,000 max · $100,000 requires revision.</Text>
                </Alert>
              </Tabs.Panel>

              {/* Vigilus RFQ */}
              <Tabs.Panel value="vigilus" p="md">
                <VigilusRFQ projectInfo={projectInfo} addOns={addOns} calc={calc} />
              </Tabs.Panel>
            </Tabs>
          </Paper>
        </Stack>
      </Container>
      <Footer platformInfo={platformInfo} />
    </Box>
  );
}

// ── Vigilus RFQ sub-component ────────────────────────────────────────────────
function VigilusRFQ({ projectInfo, addOns, calc }) {
  const customer = projectInfo.customerName || '[Customer Name]';
  const am       = projectInfo.amName || '[Account Manager]';
  const sa       = projectInfo.saName || '[Solution Architect]';
  const platform = projectInfo.platform || 'WxCC';
  const termYrs  = Math.max(1, Math.round(calc.term / 12));
  const vigilusOn = calc.vigilusServiceNow;
  const today    = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  // "Vigilus SKUs" = the Quote Summary rows whose desc is actually Vigilus-branded
  // (mirrors the same calc gates used to push those rows in the Service Transition /
  // Presidio Managed Services sections). PMN Cloud Onboarding (I26price) is excluded —
  // it's not a Vigilus product, just another Service Transition SKU.
  const hasVigilusSkus = calc.J16cmo > 0 || calc.J17cmo > 0 || calc.I27price > 0 || calc.I23price > 0 || calc.I24price > 0 || calc.I25price > 0;

  const copyEmail = () => {
    const el = document.getElementById('ecco-vigilus-rfq');
    if (el) navigator.clipboard.writeText(el.innerText).then(() => alert('Email text copied!')).catch(() => alert('Could not copy – please select manually.'));
  };

  if (!hasVigilusSkus) {
    return (
      <Alert color="gray" variant="light" title="No Vigilus SKUs Selected">
        Add a Vigilus Synthetic Transactions, Onboarding, or Training line item on the Factors tab to generate a Cloverhound quote request.
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      <Group justify="flex-end">
        <Button size="xs" variant="light" color="blue" leftSection={<IconMail size={12} />} onClick={copyEmail}>Copy Email Text</Button>
      </Group>
      <Paper withBorder radius="md" style={{ overflow: 'hidden', maxWidth: 820 }} id="ecco-vigilus-rfq">
        <Box style={{ background: '#1a3a5c', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text c="white" fw={700} size="sm">Vigilus / Cloverhound – Quote Request</Text>
          <Text c="rgba(255,255,255,0.7)" size="xs">Generated {today}</Text>
        </Box>
        <Box p="xl">
          <Table fz="xs" mb="md">
            <Table.Tbody>
              <Table.Tr><Table.Td fw={700} c="dimmed" style={{ width: 55 }}>TO:</Table.Td><Table.Td>Edward Umansky &lt;eumansky@cloverhound.com&gt; | Alonso Bogarin &lt;abogarin@cloverhound.com&gt;</Table.Td></Table.Tr>
              <Table.Tr><Table.Td fw={700} c="dimmed">FROM:</Table.Td><Table.Td>{sa || am} – Presidio</Table.Td></Table.Tr>
              <Table.Tr><Table.Td fw={700} c="dimmed">CC:</Table.Td><Table.Td>{sa && am ? `${am} – Presidio` : '[Account Manager] – Presidio'}</Table.Td></Table.Tr>
              <Table.Tr><Table.Td fw={700} c="dimmed">SUBJ:</Table.Td><Table.Td fw={700}>Quote Request – Vigilus Call Flow Tester | {customer} | {termYrs}-Year | {platform}</Table.Td></Table.Tr>
            </Table.Tbody>
          </Table>
          <Divider mb="md" />
          <Text size="sm" mb="sm">Hi Ed / Alonso,</Text>
          <Text size="sm" mb="sm">Hope you're doing well. We are working on a Managed Services proposal for <strong>{customer}</strong> on the <strong>{platform}</strong> platform and need a <strong>{termYrs}-Year quote</strong> for Vigilus Call Flow Tester. Please see the requested line items below.</Text>
          <Text size="xs" fw={700} c="blue" mb="xs">Software Subscription ({termYrs}-Year)</Text>
          <Table fz="xs" withTableBorder mb="md">
            <Table.Thead><Table.Tr><Table.Th w={60}>Qty</Table.Th><Table.Th>Description</Table.Th><Table.Th ta="right" w={100}>List Price</Table.Th></Table.Tr></Table.Thead>
            <Table.Tbody>
              {Array.from({ length: termYrs }, (_, i) => (
                <Table.Tr key={i}><Table.Td>1</Table.Td><Table.Td>Vigilus Call Flow Tester Base – 10,000 minutes per year – Year {i + 1}</Table.Td><Table.Td ta="right">$1,000</Table.Td></Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Text size="xs" fw={700} c="blue" mb="xs">Software Professional Services (One-Time)</Text>
          <Table fz="xs" withTableBorder mb="md">
            <Table.Thead><Table.Tr><Table.Th w={60}>Qty</Table.Th><Table.Th>Description</Table.Th><Table.Th ta="right" w={100}>List Price</Table.Th></Table.Tr></Table.Thead>
            <Table.Tbody>
              <Table.Tr><Table.Td>1</Table.Td><Table.Td>Call Flow Testing Setup – 1 Call Path (Includes 1 Phone Number)</Table.Td><Table.Td ta="right">$500</Table.Td></Table.Tr>
              {vigilusOn && <Table.Tr><Table.Td>1</Table.Td><Table.Td>Vigilus – ServiceNow Integration</Table.Td><Table.Td ta="right">$1,000</Table.Td></Table.Tr>}
              {Array.from({ length: addOns.vigilusSessions || 0 }, (_, i) => <Table.Tr key={i}><Table.Td>1</Table.Td><Table.Td>Vigilus – Training and Knowledge Transfer (1-hour session)</Table.Td><Table.Td ta="right">$200</Table.Td></Table.Tr>)}
            </Table.Tbody>
          </Table>
          <Alert color="blue" variant="light" p="sm" mb="md">
            <Text size="xs" fw={700} mb={4}>Notes / Assumptions:</Text>
            <Box component="ul" fz="xs" style={{ paddingLeft: 16, margin: 0 }}>
              <li>All services assume remote delivery.</li>
              <li>Presidio is the reseller/partner of record.</li>
              <li>ServiceNow integration assumes an existing ServiceNow instance is in place.</li>
              <li>Please quote all years individually in a single document (Year 1 through Year {termYrs}).</li>
              <li>Platform: <strong>{platform}</strong></li>
              <li>If partner pricing / Presidio discount is available, please apply and note separately.</li>
            </Box>
          </Alert>
          <Text size="sm" mb="xs">Please provide the quote at your earliest convenience – we are targeting submission to the customer within the next few business days.</Text>
          <Text size="sm" mb={2}>Thank you,</Text>
          <Text size="sm" fw={700}>{sa || am}</Text>
          <Text size="sm" c="dimmed">Presidio – Solution Architecture / Account Management</Text>
        </Box>
      </Paper>
    </Stack>
  );
}
